/**
 * CANONICAL STORAGE TIMELINE.
 *
 * One resolver owns the storage Start Date, End Date and Duration for the
 * whole Claim Intelligence Package™. Every chapter, KPI card, header badge,
 * narrative and PDF export must read the duration from `ReportSummary`
 * (`summary.storageDays`), which is built from this resolver — never from a
 * local calculation, an invoice quantity, or a hardcoded value.
 *
 * Storage days are ALWAYS inclusive of both the first and last calendar day
 * (Sept 8 → Sept 23 = 16 days), matching the storage benchmark engine.
 */

import { isStorageDescription, normalizeStorageLine } from "@/lib/report/storage-benchmark";

export type StorageDaysBasis =
  | "line_item_date_range"
  | "extracted_dates"
  | "raw_text_dates"
  | "extracted_storage_days"
  | "billed_quantity"
  | null;

export interface StorageTimeline {
  start: Date | null;
  end: Date | null;
  days: number | null;
  basis: StorageDaysBasis;
}

export function parseAnyStorageDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v !== "string" && typeof v !== "number") return null;
  const d = new Date(v as string | number);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Inclusive calendar-day count: the canonical ClaimGuard storage duration. */
export function inclusiveStorageDays(
  start: Date | null | undefined,
  end: Date | null | undefined,
): number | null {
  if (!start || !end) return null;
  const a = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const b = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  if (b < a) return null;
  return Math.max(1, Math.round((b - a) / 86_400_000) + 1);
}

const START_KEYS = [
  "storage_start",
  "storage_start_date",
  "storageStartDate",
  "first_storage_date",
  "tow_date",
  "towDate",
  "date_of_tow",
  "impound_date",
  "impoundDate",
  "pickup_date",
];

const END_KEYS = [
  "storage_end",
  "storage_end_date",
  "storageEndDate",
  "last_storage_date",
  "release_date",
  "pickup_release_date",
  "vehicle_release_date",
];

const STORAGE_DAY_KEYS = ["storage_days", "storageDays", "total_storage_days", "totalStorageDays"];

const STORAGE_DATE_CAPTURE =
  "([A-Za-z]{3,9}\\s+\\d{1,2},?\\s+\\d{4}|\\d{1,2}[/-]\\d{1,2}[/-]\\d{2,4}|\\d{4}-\\d{2}-\\d{2})";

export function extractStorageDatesFromRawText(rawText: unknown): {
  start: Date | null;
  end: Date | null;
} {
  if (typeof rawText !== "string" || !rawText.trim()) return { start: null, end: null };
  const normalized = rawText.replace(/\r/g, "\n").replace(/[ \t]+/g, " ");
  const patterns = [
    new RegExp(`\\bTow\\b\\s*${STORAGE_DATE_CAPTURE}\\s*\\bRelease\\b\\s*${STORAGE_DATE_CAPTURE}`, "i"),
    new RegExp(
      `Vehicle Intake Date\\s+Release\\/Invoice Date\\s+${STORAGE_DATE_CAPTURE}\\s+${STORAGE_DATE_CAPTURE}`,
      "i",
    ),
    new RegExp(
      `Storage Start(?: Date)?\\s*[:#-]?\\s*${STORAGE_DATE_CAPTURE}[\\s\\S]{0,80}?Storage End(?: Date)?\\s*[:#-]?\\s*${STORAGE_DATE_CAPTURE}`,
      "i",
    ),
  ];
  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (!match) continue;
    const start = parseAnyStorageDate(match[1]);
    const end = parseAnyStorageDate(match[2]);
    if (start || end) return { start, end };
  }
  return { start: null, end: null };
}

function pickDate(
  extracted: Record<string, unknown> | undefined | null,
  keys: string[],
): Date | null {
  if (!extracted) return null;
  for (const k of keys) {
    const d = parseAnyStorageDate(extracted[k]);
    if (d) return d;
  }
  return null;
}

function pickNumber(
  extracted: Record<string, unknown> | undefined | null,
  keys: string[],
): number | null {
  if (!extracted) return null;
  for (const k of keys) {
    const raw = extracted[k];
    const n = typeof raw === "number" ? raw : Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export interface StorageTimelineInputs {
  extracted?: Record<string, unknown> | null;
  lineItems?: Array<Record<string, unknown>> | null;
}

/**
 * Resolve Start Date, End Date and Duration from a single evidence hierarchy:
 *
 *  1. A billed date range on the storage line item (strongest — it is the
 *     vendor's own statement of the period it charged for).
 *  2. Explicit storage start / end dates extracted from the invoice.
 *  3. A storage date pair recovered from the raw invoice text.
 *  4. An extracted `storage_days` value with no dates at all.
 *
 * Whenever a real date pair exists, the duration is the inclusive day count of
 * that pair, so Start Date, End Date and Duration can never disagree.
 */
export function resolveStorageTimeline(inputs: StorageTimelineInputs): StorageTimeline {
  const extracted = inputs.extracted ?? null;
  const lineItems = inputs.lineItems ?? [];
  const statedDays = pickNumber(extracted, STORAGE_DAY_KEYS);

  /* A duration the vendor states on its own invoice is only overridden when it
     materially contradicts the documented storage period. An off-by-one gap is
     a first/last-day counting convention, not a data-integrity defect, so the
     stated figure is kept and the report never contradicts the source document. */
  const reconcile = (derived: number): number =>
    statedDays !== null && Math.abs(statedDays - derived) <= 1 ? statedDays : derived;

  // 1. Storage line item carrying its own billed date range.
  const storageLine = lineItems.find((li) => {
    const description = typeof li?.description === "string" ? li.description : "";
    return isStorageDescription(description) && /through|thru|\d\/\d/.test(description);
  });
  if (storageLine) {
    const normalized = normalizeStorageLine({
      description: String(storageLine.description ?? ""),
      quantity: typeof storageLine.quantity === "number" ? storageLine.quantity : null,
      unitPrice: typeof storageLine.unitPrice === "number" ? storageLine.unitPrice : null,
      total: typeof storageLine.total === "number" ? storageLine.total : null,
    });
    if (normalized.durationBasis === "line_item_date_range" && normalized.durationDays !== null) {
      const start = parseAnyStorageDate(normalized.startDate);
      const end = parseAnyStorageDate(normalized.endDate);
      return {
        start: start ?? pickDate(extracted, START_KEYS),
        end: end ?? pickDate(extracted, END_KEYS),
        days: reconcile(inclusiveStorageDays(start, end) ?? normalized.durationDays),
        basis: "line_item_date_range",
      };
    }
  }

  // 2. Explicit extracted storage dates.
  const extractedStart = pickDate(extracted, START_KEYS);
  const extractedEnd = pickDate(extracted, END_KEYS);
  const extractedPairDays = inclusiveStorageDays(extractedStart, extractedEnd);
  if (extractedPairDays !== null) {
    return {
      start: extractedStart,
      end: extractedEnd,
      days: reconcile(extractedPairDays),
      basis: "extracted_dates",
    };
  }

  // 3. Storage dates recovered from raw invoice text.
  const rawDates = extractStorageDatesFromRawText(extracted?.raw_text);
  const rawPairDays = inclusiveStorageDays(rawDates.start, rawDates.end);
  if (rawPairDays !== null) {
    return {
      start: rawDates.start,
      end: rawDates.end,
      days: reconcile(rawPairDays),
      basis: "raw_text_dates",
    };
  }

  // 4. A stated storage-day count with no usable date pair.
  return {
    start: extractedStart ?? rawDates.start,
    end: extractedEnd ?? rawDates.end,
    days: statedDays,
    basis: statedDays !== null ? "extracted_storage_days" : null,
  };
}

